import { type WorkspaceRepository } from '@telespot/domain';

export interface ListWorkspacesInput {
  organizationId: string;
  limit: number;
  caseTypeId?: string;
  searchTerm?: string;
  lastItemCreatedAt?: Date;
}

export interface ListWorkspaceItemOutput {
  id: string;
  name: string;
  organizationId: string;
  caseTypeId: string;
  usedStorage: number;
  numMembers?: number;
  numCases?: number;
  createdAt: Date;
}

/**
 * Thrown when listing workspaces fails (e.g. repository error).
 */
export class ListWorkspacesQueryError extends Error {
  constructor(
    message = 'Failed to list workspaces',
    public readonly cause?: unknown
  ) {
    super(message);
    this.name = 'ListWorkspacesQueryError';
    Object.setPrototypeOf(this, ListWorkspacesQueryError.prototype);
  }
}

/**
 * Use case to list workspaces in an organization with optional search and cursor pagination.
 */
export interface IListWorkspacesQuery {
  /**
   * List workspaces matching the given criteria.
   * @param input Organization id, limit, optional search term and pagination cursor.
   * @returns The list items.
   * @throws ListWorkspacesQueryError when the operation fails.
   */
  execute(input: ListWorkspacesInput): Promise<ListWorkspaceItemOutput[]>;
}

export class ListWorkspacesQuery implements IListWorkspacesQuery {
  constructor(private readonly workspaceRepository: WorkspaceRepository) {}

  public async execute(input: ListWorkspacesInput): Promise<ListWorkspaceItemOutput[]> {
    try {
      const workspaces = await this.workspaceRepository.list(
        input.organizationId,
        input.limit,
        input.caseTypeId,
        input.searchTerm,
        input.lastItemCreatedAt
      );
      return workspaces.map((workspace) => ({
        id: workspace.id,
        name: workspace.name,
        organizationId: workspace.organizationId,
        caseTypeId: workspace.caseTypeId,
        usedStorage: workspace.usedStorage,
        numMembers: workspace.numMembers,
        numCases: workspace.numCases,
        createdAt: workspace.createdAt ?? new Date(),
      }));
    } catch (error) {
      throw new ListWorkspacesQueryError('Failed to list workspaces', error);
    }
  }
}
